import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

export default function WorkflowSenderPool({
  workflow,
  senders,
  api,
  onChanged,
}) {
  const [selected, setSelected] = useState(workflow.senders || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => setSelected(workflow.senders || []), [workflow]);

  const save = async () => {
    setSaving(true);
    try {
      const response = await api.setWorkflowSenders(
        workflow.id,
        selected.map((sender) => sender.id),
      );
      if (!response.success) {
        throw new Error(
          response.errors?.[0]?.message || "Unable to save senders.",
        );
      }
      enqueueSnackbar("Workflow senders saved.", { variant: "success" });
      await onChanged();
    } catch (requestError) {
      enqueueSnackbar(requestError.message, { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6">Workflow senders</Typography>
      <Typography color="text.secondary" mb={2}>
        Each participant is assigned one available sender for the journey. If
        that sender becomes unavailable, another profile from this pool is used.
      </Typography>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1}
        alignItems="center"
      >
        <Autocomplete
          multiple
          fullWidth
          options={senders}
          value={selected}
          getOptionLabel={(option) =>
            `${option.displayName} <${option.fromEmail}>`
          }
          getOptionDisabled={(option) =>
            !option.isActive || !option.credentialsConfigured
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => setSelected(value)}
          renderInput={(params) => (
            <TextField {...params} label="Sender profiles" />
          )}
        />
        <Button
          variant="contained"
          onClick={save}
          disabled={saving}
          sx={{ whiteSpace: "nowrap", minWidth: 130 }}
        >
          Save senders
        </Button>
      </Stack>
      {selected.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" mt={1.5}>
          {selected.map((sender) => {
            const allocation = sender.allocations?.find(
              (item) => item.workflow.id === workflow.id,
            );
            return (
              <Chip
                key={sender.id}
                size="small"
                variant="outlined"
                label={`${sender.name}: ${allocation?.percentage ?? "—"}% allocation`}
              />
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}
